import { Controller } from "@hotwired/stimulus";

/*
 * Reveals the one row of a pair that this browser can actually use.
 *
 * The Help section offers to take feedback two ways, and only one of them is real on any given
 * machine: an iPhone can open the App Store's review sheet and nothing else can, so offering
 * "Rate the app" on a desktop is offering a dead end, and offering a mail client to somebody
 * holding a phone with the app on it asks for the review in the place it is worth least.
 *
 * Both rows are server-rendered `hidden` and this removes the attribute from one. The other
 * direction -- render both and hide one -- fails the only way that matters: a reader with no
 * script would see both rows, and two contradictory offers read as a bug, where seeing neither
 * reads as a section that is simply shorter.
 *
 * It cannot be done on the server either. The user agent is the one thing that would decide it
 * there, and the pages behind this menu are cached per URL rather than per agent.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["row"];

  declare readonly rowTargets: HTMLElement[];

  override connect(): void {
    const platform = this.platform();

    for (const row of this.rowTargets) {
      if (row.dataset.platformRow === platform) {
        row.removeAttribute("hidden");
      }
    }
  }

  /**
   * iPhone and iPad, where the App Store is a tap away.
   *
   * The second test is iPadOS 13 and later, which reports itself as a Mac down to the user-agent
   * string: `MacIntel` with a touch screen is an iPad, because no Mac has ever had one.
   */
  private platform(): "ios" | "other" {
    const agent = window.navigator.userAgent;
    const iPadOS =
      window.navigator.platform === "MacIntel" &&
      window.navigator.maxTouchPoints > 1;

    return /iPhone|iPad|iPod/.test(agent) || iPadOS ? "ios" : "other";
  }
}
